import React, { useState, useEffect } from 'react';

const Schedule = () => {
    const [schedule, setSchedule] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [scheduleData, setScheduleData] = useState({});
    const [availableOrders, setAvailableOrders] = useState([]);
    const [notification, setNotification] = useState(null);

    const apiUrl = import.meta.env.VITE_API_URL;
    const scheduleEndpoint = import.meta.env.VITE_get_weekly_schedule;
    const restaurantsEndpoint = import.meta.env.VITE_get_all_menus;
    const updateScheduleEndpoint = import.meta.env.VITE_update_weekly_schedule;

    const daysOfWeek = [
        { key: 'Sunday', label: 'الأحد' },
        { key: 'Monday', label: 'الإثنين' },
        { key: 'Tuesday', label: 'الثلاثاء' },
        { key: 'Wednesday', label: 'الأربعاء' },
        { key: 'Thursday', label: 'الخميس' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [scheduleResponse, restaurantsResponse] = await Promise.all([
                fetch(`${apiUrl}${scheduleEndpoint}`),
                fetch(`${apiUrl}${restaurantsEndpoint}`)
            ]);

            if (scheduleResponse.ok && restaurantsResponse.ok) {
                const scheduleData = await scheduleResponse.json();
                const restaurantsData = await restaurantsResponse.json();

                setSchedule(scheduleData);
                setRestaurants(restaurantsData);

                // Extract unique orders from schedule data
                const orders = [...new Set(scheduleData.map(item => item.order))].sort((a, b) => a - b);
                setAvailableOrders(orders);

                // Initialize schedule data structure using order from API
                const initialScheduleData = {};
                daysOfWeek.forEach(day => {
                    initialScheduleData[day.key] = {};
                });

                // Populate with existing data from API response using order field
                scheduleData.forEach(item => {
                    if (initialScheduleData[item.dayOfWeek]) {
                        initialScheduleData[item.dayOfWeek][item.order] = item.menuId.toString();
                    }
                });

                setScheduleData(initialScheduleData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleScheduleChange = (day, order, value) => {
        setScheduleData(prev => {
            const newDayData = { ...prev[day] };

            if (value === '') {
                // Remove the order if empty option is selected
                delete newDayData[order];
            } else {
                // Set the menuId if a restaurant is selected
                newDayData[order] = value;
            }

            return {
                ...prev,
                [day]: newDayData
            };
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            // Convert schedule data to API format using order field
            const schedulesToUpdate = [];
            daysOfWeek.forEach(day => {
                Object.keys(scheduleData[day.key] || {}).forEach(order => {
                    const menuId = scheduleData[day.key][order];
                    if (menuId && menuId !== '') {
                        schedulesToUpdate.push({
                            dayOfWeek: day.key,
                            order: parseInt(order),
                            menuId: parseInt(menuId)
                        });
                    }
                });
            });

            const response = await fetch(`${apiUrl}${updateScheduleEndpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ schedules: schedulesToUpdate })
            });

            if (response.ok) {
                setNotification({ 'message': 'تم التعديل بنجاح', 'type': 'success' });
                // await fetchData(); // Refresh data
            } else {
                setNotification({ 'message': 'حدث خطأ أثناء حفظ الجدول', 'type': 'error' });
            }
        } catch (error) {
            console.error('Error saving schedule:', error);
            setNotification({ 'message': 'حدث خطأ أثناء حفظ الجدول', 'type': 'error' });
        } finally {
            setSaving(false);
        }
    };

    const getOrderLabel = (order) => {
        // Convert order to Arabic number for dynamic labeling
        const arabicNumbers = {
            1: 'الأول',
            2: 'الثاني',
            3: 'الثالث',
            4: 'الرابع',
            5: 'الخامس',
            6: 'السادس',
            7: 'السابع',
            8: 'الثامن',
            9: 'التاسع',
            10: 'العاشر'
        };

        if (arabicNumbers[order] !== undefined) {
            return `المطعم ${arabicNumbers[order]}`;
        }

        // Fallback for orders beyond 9
        return `مطعم ${order}`;
    };

    const showNotification = (message, type) => {
        setNotification({ message, type });
        // Auto-hide notification after 3 seconds
        setTimeout(() => {
            setNotification(null);
        }, 3000);
    };

    if (loading) {
        return (
            <section className="main_content">
                <div className="container">
                    <div className="loading-message">جاري التحميل...</div>
                </div>
            </section>
        );
    }

    return (
        <section className="main_content">
            <div className="container">
                <h1 className="page-title">جدول المطاعم</h1>

                {/* Notification Box */}
                {notification && (
                    <div className={`alert ${notification.type}`}>
                        {notification.message}
                    </div>
                )}

                <div >
                    <table className="table">
                        <thead>
                            <tr>
                                <th >اليوم</th>
                                {availableOrders.map(order => (
                                    <th key={order}>
                                        {getOrderLabel(order)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {daysOfWeek.map((day, index) => (
                                <tr key={day.key}>
                                    <td >{day.label}</td>

                                    {/* Generate restaurant cells dynamically based on available orders from API */}
                                    {availableOrders.map(order => (
                                        <td key={order} >
                                            <select
                                                className="form-select"
                                                value={scheduleData[day.key]?.[order] || ''}
                                                onChange={(e) => handleScheduleChange(day.key, order, e.target.value)}
                                            >
                                                <option value="">اختر مطعم</option>
                                                {restaurants.map(restaurant => (
                                                    <option key={restaurant.menuID} value={restaurant.menuID}>
                                                        {restaurant.menuName}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="action-buttons">
                        <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'جاري الحفظ...' : 'حفظ'}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Schedule;
